import React, { useState, useEffect, useCallback } from 'react';
import { SlidersHorizontal, X } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { getProducts, getProductsByCategory, searchProducts, getProductsByFilters, getBrandsList, getSpecifications } from '../api/productApi';

const RefurbishedLaptops = ({ onCartUpdate }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ── Pagination states ────────────────────────────────
  const [page, setPage] = useState(1);
  const [limit] = useState(12);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [sortBy, setSortBy] = useState('relevance');
  const [searchQuery, setSearchQuery] = useState('');

  const [priceRange, setPriceRange] = useState([0, 200000]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedRAM, setSelectedRAM] = useState([]);
  const [selectedStorage, setSelectedStorage] = useState([]);
  const [selectedProcessor, setSelectedProcessor] = useState([]);
  const [selectedScreen, setSelectedScreen] = useState([]);

  // Filter options state
  const [brands, setBrands] = useState([]);
  const [ramOptions, setRamOptions] = useState([]);
  const [storageOptions, setStorageOptions] = useState([]);
  const [processorOptions, setProcessorOptions] = useState([]);
  const [screenOptions, setScreenOptions] = useState([]);

  // Fetch filter options from backend
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const [brandsRes, specsRes] = await Promise.all([
          getBrandsList(),
          getSpecifications()
        ]);

        if (brandsRes.success && Array.isArray(brandsRes.data)) {
          setBrands(brandsRes.data.map(brand => brand.name));
        }

        if (specsRes.success && specsRes.data) {
          setRamOptions(specsRes.data.ram || []);
          setStorageOptions(specsRes.data.storage || []);
          setProcessorOptions(specsRes.data.processor || []);
          setScreenOptions(specsRes.data.screenSize || []);
        }
      } catch (error) {
        console.error('Error fetching filter options:', error);
        // Fallback or leave empty? User requested backend usage, so we'll log error properly.
        // Maybe set some defaults if critical? But better to rely on backend.
      }
    };

    fetchFilterOptions();
  }, []);

  // Reset page to 1 when filters/sort/search change
  useEffect(() => {
    setPage(1);
  }, [
    sortBy, searchQuery, priceRange, selectedBrands,
    selectedRAM, selectedStorage, selectedProcessor, selectedScreen
  ]);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const filters = {
        page,
        limit,
        isActive: 'true',
        sort: sortBy,
        condition: 'refurbished'
      };

      if (priceRange[0] > 0) filters.minPrice = priceRange[0];
      if (priceRange[1] < 200000) filters.maxPrice = priceRange[1];
      if (selectedBrands.length) filters.brand = selectedBrands.join(',');
      if (selectedRAM.length) filters.ram = selectedRAM.join(',');
      if (selectedStorage.length) filters.storage = selectedStorage.join(',');
      if (selectedProcessor.length) filters.processor = selectedProcessor.join(',');
      if (selectedScreen.length) filters.screenSize = selectedScreen.join(',');
      if (searchQuery.trim()) filters.search = searchQuery.trim();

      const response = await getProductsByFilters(filters);

      if (response.success) {
        // Backend now handles condition filtering, so pagination is correct
        setProducts(response.data.products);
 console.log(response.pagination)
        // Save pagination metadata
        
   
      setTotalProducts(response.pagination?.total || 0);
setTotalPages(response.pagination?.pages || 1);
      } else {
        setProducts([]);
        setTotalProducts(0);
        setTotalPages(1);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch products');
      setProducts([]);
      setTotalProducts(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [
    page, limit, sortBy,
    priceRange, selectedBrands,
    selectedRAM, selectedStorage,
    selectedProcessor, selectedScreen,
    searchQuery
  ]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const removeFilter = (type, value = null) => {
    if (type === 'brand') setSelectedBrands(prev => prev.filter(b => b !== value));
    if (type === 'ram') setSelectedRAM(prev => prev.filter(r => r !== value));
    if (type === 'storage') setSelectedStorage(prev => prev.filter(s => s !== value));
    if (type === 'processor') setSelectedProcessor(prev => prev.filter(p => p !== value));
    if (type === 'screen') setSelectedScreen(prev => prev.filter(s => s !== value));
    if (type === 'search') setSearchQuery('');
    if (type === 'price') setPriceRange([0, 200000]);
  };

  const clearAllFilters = () => {
    setPriceRange([0, 200000]);
    setSelectedBrands([]);
    setSelectedRAM([]);
    setSelectedStorage([]);
    setSelectedProcessor([]);
    setSelectedScreen([]);
    setSearchQuery('');
  };

  const FilterSection = ({ title, options, selected, setSelected }) => (
    <div className="mb-6">
      <h3 className="font-semibold mb-3 dark:text-white">{title}</h3>
      <div className="space-y-2">
        {options.map((option) => (
          <div key={option} className="flex items-center space-x-2">
            <Checkbox
              checked={selected.includes(option)}
              onCheckedChange={(checked) => {
                if (checked) setSelected([...selected, option]);
                else setSelected(selected.filter(item => item !== option));
              }}
            />
            <label className="text-sm cursor-pointer dark:text-gray-300">{option}</label>
          </div>
        ))}
      </div>
    </div>
  );

  const PaginationControls = () => {
    if (totalPages <= 1) return null;

    const renderPageNumbers = () => {
      const pages = [];
      const showEllipsis = totalPages > 7;

      if (!showEllipsis) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Logic for sliding window with ellipsis
        if (page <= 4) {
          for (let i = 1; i <= 5; i++) pages.push(i);
          pages.push('...');
          pages.push(totalPages);
        } else if (page >= totalPages - 3) {
          pages.push(1);
          pages.push('...');
          for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
        } else {
          pages.push(1);
          pages.push('...');
          pages.push(page - 1);
          pages.push(page);
          pages.push(page + 1);
          pages.push('...');
          pages.push(totalPages);
        }
      }

      return pages.map((p, idx) => (
        <Button
          key={idx}
          variant={p === page ? "default" : "outline"}
          size="sm"
          className={`w-9 h-9 p-0 ${p === '...' ? "cursor-default hover:bg-transparent" : ""}`}
          disabled={p === '...' || loading}
          onClick={() => typeof p === 'number' && setPage(p)}
        >
          {p}
        </Button>
      ));
    };

    return (
      <div className="mt-10 flex items-center justify-center gap-2 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          disabled={page === 1 || loading}
          onClick={() => setPage(p => Math.max(1, p - 1))}
        >
          Previous
        </Button>

        <div className="flex gap-1 mx-2">
          {renderPageNumbers()}
        </div>

        <Button
          variant="outline"
          size="sm"
          disabled={page === totalPages || loading}
          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
        >
          Next
        </Button>
      </div>
    );
  };

  return (
    <div className="py-12 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="container mx-auto px-4">

        <h1 className="text-4xl font-bold mb-6 dark:text-white">Refurbished Laptops</h1>

        {/* Search & Sort */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-4 flex gap-4">
          <Input
            placeholder="Search laptops by name, brand, or price..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="relevance">Relevance</SelectItem>
              <SelectItem value="price-low">Price: Low to High</SelectItem>
              <SelectItem value="price-high">Price: High to Low</SelectItem>
              <SelectItem value="rating">Highest Rated</SelectItem>
              <SelectItem value="discount">Best Discount</SelectItem>
              <SelectItem value="newest">Newest First</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Applied Filters */}
        {(selectedBrands.length ||
          selectedRAM.length ||
          selectedStorage.length ||
          selectedProcessor.length ||
          selectedScreen.length ||
          searchQuery ||
          priceRange[0] > 0 ||
          priceRange[1] < 200000) && (
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm mb-6">
              <div className="flex flex-wrap gap-2">
                {searchQuery && (
                  <Badge className="flex items-center gap-2">
                    Search: {searchQuery}
                    <X size={14} onClick={() => removeFilter('search')} />
                  </Badge>
                )}

                {(priceRange[0] > 0 || priceRange[1] < 200000) && (
                  <Badge className="flex items-center gap-2">
                    ₹{priceRange[0]} - ₹{priceRange[1]}
                    <X size={14} onClick={() => removeFilter('price')} />
                  </Badge>
                )}

                {selectedBrands.map(b => (
                  <Badge key={b} className="flex items-center gap-2">
                    {b}
                    <X size={14} onClick={() => removeFilter('brand', b)} />
                  </Badge>
                ))}

                {selectedRAM.map(r => (
                  <Badge key={r} className="flex items-center gap-2">
                    {r}
                    <X size={14} onClick={() => removeFilter('ram', r)} />
                  </Badge>
                ))}

                {selectedStorage.map(s => (
                  <Badge key={s} className="flex items-center gap-2">
                    {s}
                    <X size={14} onClick={() => removeFilter('storage', s)} />
                  </Badge>
                ))}

                {selectedProcessor.map(p => (
                  <Badge key={p} className="flex items-center gap-2">
                    {p}
                    <X size={14} onClick={() => removeFilter('processor', p)} />
                  </Badge>
                ))}

                {selectedScreen.map(s => (
                  <Badge key={s} className="flex items-center gap-2">
                    {s}
                    <X size={14} onClick={() => removeFilter('screen', s)} />
                  </Badge>
                ))}

                <Button size="sm" variant="ghost" onClick={clearAllFilters}>
                  Clear All
                </Button>
              </div>
            </div>
          )}

        {/* Filters + Grid Layout */}
        <div className="flex gap-6">

          {/* Sidebar */}
          <div className="hidden lg:block w-64 bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
            <h2 className="font-bold mb-4 dark:text-white">Filters</h2>

        <div className="mb-6">
  <h3 className="font-semibold mb-3 dark:text-white">Price Range</h3>

  {/* Selected Values Display */}
  <div className="flex justify-between text-sm font-medium mb-2 dark:text-gray-200">
    <span>₹{priceRange[0].toLocaleString()}</span>
    <span>₹{priceRange[1].toLocaleString()}</span>
  </div>

  <Slider
    min={0}
    max={200000}
    step={1000}
    value={priceRange}
    onValueChange={setPriceRange}
  />

  {/* Absolute Min / Max Info */}
  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-2">
    <span>Min: ₹0</span>
    <span>Max: ₹2,00,000</span>
  </div>
</div>

            <FilterSection title="Brand" options={brands} selected={selectedBrands} setSelected={setSelectedBrands} />
            <FilterSection title="RAM" options={ramOptions} selected={selectedRAM} setSelected={setSelectedRAM} />
            <FilterSection title="Storage" options={storageOptions} selected={selectedStorage} setSelected={setSelectedStorage} />
            <FilterSection title="Processor" options={processorOptions} selected={selectedProcessor} setSelected={setSelectedProcessor} />
            <FilterSection title="Screen Size" options={screenOptions} selected={selectedScreen} setSelected={setSelectedScreen} />
          </div>

          {/* Products + Pagination */}
          <div className="flex-1">
            {loading ? (
              <div className="text-center py-20 bg-white dark:bg-gray-800 dark:text-gray-300 rounded-lg">Loading...</div>
            ) : error ? (
              <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-lg text-red-500">{error}</div>
            ) : products.length ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                  {products.map(product => (
                    <ProductCard
                      key={product._id}
                      product={product}
                      onAddToCart={onCartUpdate}
                    />
                  ))}
                </div>

                <PaginationControls />
              </>
            ) : (
              <div className="text-center py-20 bg-white dark:bg-gray-800 dark:text-gray-300 rounded-lg">
                No products found
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default RefurbishedLaptops;